/**
 * RNR's Input, on the design file's Field spec: --card for the control surface, --input for its
 * border, and a focus state that draws a 3px --accent ring rather than moving anything.
 *
 * The surface is --card and NOT --popover, which is what it used to be. In this restyle --popover
 * is the DOCK, and Tuxedo's dock is midnight in both modes — so a field on that token rendered as
 * a solid black box in the light room. A field sits on a card; it takes the card's surface.
 */
import { useFieldEdge } from '@/lib/edges';
import { familyFor } from '@/lib/fonts';
import { cn } from '@/lib/utils';
import { Platform, TextInput } from 'react-native';

function Input({ className, ...props }: React.ComponentProps<typeof TextInput> & React.RefAttributes<TextInput>) {
  // A field the user types into is the one place a fallback font is most obvious, because it
  // sits next to a label that is already in the right one. Native needs the family by name.
  const fontFamily = familyFor('sans', 400);
  // --input against --card is 1.75:1 in the dark rooms, which is a field with no edge at all.
  const edge = useFieldEdge();
  return (
    <TextInput
      className={cn(
        'bg-card text-foreground text-body font-sans h-11 w-full min-w-0 flex-row items-center rounded-md border px-3 py-2',
        edge,
        props.editable === false && cn('bg-muted text-muted-foreground', Platform.select({ web: 'cursor-not-allowed' })),
        Platform.select({
          web: cn(
            'placeholder:text-muted-foreground/70 selection:bg-accent outline-none transition-[color,box-shadow]',
            'focus-visible:border-ring focus-visible:ring-accent focus-visible:ring-[3px]',
            'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
          ),
          native: 'placeholder:text-muted-foreground/70',
        }),
        className,
      )}
      {...props}
      style={fontFamily ? [{ fontFamily }, props.style] : props.style}
    />
  );
}

export { Input };
